import { useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { REFLECTION_EXERCISES, type ExerciseAnswerInput, type ExercisePauseState, type ReflectionState } from "@calega/content";
import { api } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { ReflectionIcon } from "../components/ReflectionSettingsDialog";
import { ExercisePauseActionContext, ExercisePauseVisibilityContext } from "./ExercisePauseBoundary";
import { registerReflectionHandler } from "./reflection-request";

export function ExerciseReflectionBoundary({ runId, exerciseType, children }: { runId: string; exerciseType: string; children: ReactNode }) {
  const realtime = useRealtime();
  const beginPause = useContext(ExercisePauseActionContext);
  const paused = useContext(ExercisePauseVisibilityContext);
  const latest = useRef(realtime); latest.current = realtime;
  const [state, setState] = useState<ReflectionState | null>(null), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const current = useRef(state); current.current = state;
  const pending = useRef<{ resolve(): void; reject(cause: Error): void } | null>(null);
  const opening = useRef(false);
  const approvedGroup = useRef("");
  const [preparing, setPreparing] = useState(false);
  const [, tick] = useState(0);
  const offset = useRef(0);
  const panel = useRef<HTMLElement>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const task = useRef<HTMLFieldSetElement>(null);
  const [readingSpace, setReadingSpace] = useState(0);
  const returnFocus = useRef<HTMLElement | null>(null);
  const supported = REFLECTION_EXERCISES.some((entry) => entry.type === exerciseType);
  const sessionId = realtime.session?.id;
  const path = (id: string) => `/learning/runs/${encodeURIComponent(id)}/reflection${latest.current.session ? `?sessionId=${encodeURIComponent(latest.current.session.id)}` : ""}`;
  function accept(next: ReflectionState) {
    if (current.current?.runId === next.runId && (current.current.revision > next.revision || current.current.pause.revision > next.pause.revision)) return;
    offset.current = next.pause.serverNow - Date.now();
    current.current = next; setState(next);
    window.dispatchEvent(new CustomEvent("calepia:pause-state", { detail: next.pause }));
  }
  function cancel() { pending.current?.reject(new Error("")); pending.current = null; }
  async function command(id: string, body: unknown) {
    const next = await api<ReflectionState>(path(id), { method: "POST", body: JSON.stringify(body), signal: AbortSignal.timeout(12_000) });
    accept(next); return next;
  }
  useEffect(() => {
    if (!supported || !runId) return;
    let closed = false, reading = false;
    const read = async () => {
      if (reading || document.visibilityState !== "visible") return;
      reading = true;
      try {
        const next = await api<ReflectionState>(path(runId), { signal: AbortSignal.timeout(5_000) });
        if (!closed && !(current.current?.runId === next.runId && (current.current.revision > next.revision || current.current.pause.revision > next.pause.revision))) {
          if (current.current?.active && !next.active && !opening.current && !next.approved) cancel();
          if (next.approved && pending.current && !opening.current) { pending.current.resolve(); pending.current = null; }
          accept(next);
        }
      } catch (cause) { if (!closed && current.current?.active) setError(cause instanceof Error ? cause.message : "Die Verbindung wird geprüft."); }
      finally { reading = false; }
    };
    const update = (event: Event) => {
      const value = (event as CustomEvent<ExercisePauseState>).detail;
      if (value.runId === runId && value.reflection && (value.reflection.revision !== current.current?.revision || value.revision !== current.current?.pause.revision)) void read();
    };
    void read();
    const timer = window.setInterval(() => void read(), 5_000);
    window.addEventListener("calepia:pause-state", update); window.addEventListener("online", read); window.addEventListener("focus", read); document.addEventListener("visibilitychange", read);
    return () => { closed = true; clearInterval(timer); window.removeEventListener("calepia:pause-state", update); window.removeEventListener("online", read); window.removeEventListener("focus", read); document.removeEventListener("visibilitychange", read); cancel(); current.current = null; setState(null); };
  }, [runId, sessionId, supported]);

  useLayoutEffect(() => {
    let disposed = false;
    const unregister = registerReflectionHandler(async (id, answers: ExerciseAnswerInput[]) => {
      if (!supported || (current.current?.runId === id && !current.current.enabled && !current.current.active) || (answers.length > 0 && answers.every((answer) => "skipped" in answer && answer.skipped))) return;
      if (opening.current || pending.current) throw new Error("Die Prüfung ist bereits geöffnet.");
      returnFocus.current = document.activeElement as HTMLElement | null;
      opening.current = true; setPreparing(true); setError("");
      window.dispatchEvent(new CustomEvent("calepia:reflection-preparing", { detail: { runId: id, active: true } }));
      try {
        if (latest.current.session) {
          const deadline = performance.now() + 12_000;
          while (!latest.current.synchronized) {
            if (!latest.current.connected || performance.now() >= deadline) throw new Error("Die Eingaben sind noch nicht synchronisiert. Bitte prüfe die Verbindung.");
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }
        const before = current.current?.runId === id ? current.current : await api<ReflectionState>(path(id));
        const next = await command(id, { action: "begin", requestId: crypto.randomUUID(), revision: before.revision, answers });
        if (disposed) throw new Error("");
        approvedGroup.current = JSON.stringify(answers);
        if (!next.required || next.approved) return;
        await new Promise<void>((resolve, reject) => { pending.current = { resolve, reject }; opening.current = false; setPreparing(false); });
      } finally { opening.current = false; setPreparing(false); window.dispatchEvent(new CustomEvent("calepia:reflection-preparing", { detail: { runId: id, active: false } })); }
    });
    return () => { disposed = true; unregister(); cancel(); };
  }, [supported, exerciseType, sessionId]);

  useEffect(() => {
    const delivered = (event: Event) => {
      const detail = (event as CustomEvent<{ runId: string; inputs: ExerciseAnswerInput[] }>).detail;
      if (detail.runId !== current.current?.runId || !current.current?.approved || JSON.stringify(detail.inputs) !== approvedGroup.current) return;
      const next = { ...current.current, approved: false, revision: current.current.revision + 1 };
      current.current = next; setState(next);
    };
    window.addEventListener("calepia:reflection-delivered", delivered);
    return () => window.removeEventListener("calepia:reflection-delivered", delivered);
  }, []);

  // A professional's saved answer wins over an open learner confirmation.
  useEffect(() => {
    const envelope = realtime.state.exercise as { state?: { phase?: string; submitted?: boolean } } | null;
    if (pending.current && (envelope?.state?.phase === "feedback" || envelope?.state?.phase === "complete" || envelope?.state?.submitted)) {
      cancel(); setState((value) => value ? { ...value, active: false } : value);
    }
  }, [realtime.state.exercise]);
  const active = Boolean(state?.active && state.required), locked = active || preparing;
  const visible = active && !paused, showingPreparation = preparing && !active && !paused;
  useLayoutEffect(() => {
    if (!visible && !showingPreparation) return;
    const element = overlay.current;
    if (!element) return;
    const header = document.querySelector(".app-header"), viewport = window.visualViewport;
    const existingGap = Math.max(0, (task.current?.parentElement?.getBoundingClientRect().top ?? 0) - Math.max(viewport?.offsetTop ?? 0, header?.getBoundingClientRect().bottom ?? 0) - 8);
    let frame = 0;
    const layout = (positionSolution = false) => {
      const viewTop = viewport?.offsetTop ?? 0;
      const top = Math.max(viewTop, header?.getBoundingClientRect().bottom ?? 0) + 8;
      element.style.setProperty("--reflection-top", `${top}px`);
      element.style.setProperty("--reflection-available", `${Math.max(0, viewTop + (viewport?.height ?? innerHeight) - top - 8)}px`);
      setReadingSpace(Math.max(0, element.getBoundingClientRect().height + 16 - existingGap));
      if (!positionSolution) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!visible) return;
        const previous = returnFocus.current;
        if (!previous || !task.current?.contains(previous)) return;
        const target = previous.matches("input, textarea, select") ? previous
          : task.current.querySelector<HTMLElement>('input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea, select') ?? previous;
        const freeTop = element.getBoundingClientRect().bottom + 12;
        const freeBottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? innerHeight) - 12;
        const bounds = target.getBoundingClientRect();
        if (bounds.top >= freeTop && bounds.bottom <= freeBottom) return;
        target.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
        let scroller = target.parentElement;
        while (scroller && !(scroller.scrollHeight > scroller.clientHeight && /auto|scroll/.test(getComputedStyle(scroller).overflowY))) scroller = scroller.parentElement;
        const rect = target.getBoundingClientRect();
        (scroller ?? document.scrollingElement)?.scrollBy({ top: rect.top + rect.height / 2 - (freeTop + freeBottom) / 2, behavior: "instant" });
      });
    };
    layout(true);
    const resize = () => layout(true);
    const observer = new ResizeObserver(() => layout());
    observer.observe(element); if (header) observer.observe(header);
    window.addEventListener("resize", resize); viewport?.addEventListener("resize", resize); viewport?.addEventListener("scroll", resize);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); window.removeEventListener("resize", resize); viewport?.removeEventListener("resize", resize); viewport?.removeEventListener("scroll", resize); };
  }, [visible, showingPreparation]);
  useLayoutEffect(() => { if (visible) panel.current?.focus({ preventScroll: true }); }, [visible]);
  useEffect(() => {
    if (!active) return;
    const previous = returnFocus.current;
    const timer = window.setInterval(() => tick((value) => value + 1), 200);
    return () => { clearInterval(timer); window.requestAnimationFrame(() => {
      if (!previous?.isConnected || current.current?.active) return;
      previous.focus({ preventScroll: true });
      const rect = previous.getBoundingClientRect();
      const viewTop = window.visualViewport?.offsetTop ?? 0;
      if (rect.top < Math.max(viewTop, document.querySelector(".app-header")?.getBoundingClientRect().bottom ?? 0) || rect.bottom > viewTop + (window.visualViewport?.height ?? innerHeight)) previous.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
    }); };
  }, [active]);
  async function decide(action: "confirm" | "edit") {
    if (!state || busy) return;
    setBusy(true); setError("");
    try {
      await command(state.runId, { action, revision: state.revision });
      const waiting = pending.current; pending.current = null;
      if (action === "confirm") {
        if (waiting) waiting.resolve();
        else setError("Bitte überprüfe deine Lösung erneut, um die Abgabe fortzusetzen.");
      } else waiting?.reject(new Error(""));
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Die Prüfung konnte nicht bestätigt werden."); }
    finally { setBusy(false); }
  }
  const remaining = state?.until ? Math.max(0, Math.ceil((state.until - Date.now() - offset.current) / 1000)) : 0;
  const ready = remaining === 0 && !state?.paused && !busy;
  return <div className={locked ? "reflection-reading" : ""} style={{ "--reflection-height": `${readingSpace}px` } as CSSProperties}>
    {state?.active && !state.required && <p className="alert info" role="status">Das Kind schaut seine Lösung noch einmal an. Du kannst als Fachkraft weiterhin sofort prüfen.</p>}
    <fieldset ref={task} className="reflection-task" disabled={locked} aria-disabled={locked || undefined} onClickCapture={(event) => { if (locked) { event.preventDefault(); event.stopPropagation(); } }} onKeyDownCapture={(event) => { if (locked) { event.preventDefault(); event.stopPropagation(); } }}>{children}</fieldset>
    {(visible || showingPreparation) && createPortal(<div ref={overlay} className="reflection-overlay">
    {showingPreparation && <p className="reflection-preparing" role="status">Die Denkpause wird vorbereitet …</p>}
    {visible && <section ref={panel} tabIndex={-1} className="reflection-panel" role="dialog" aria-modal="false" aria-labelledby="reflection-title" onKeyDown={(event) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); }
      if (event.key !== "Tab") return;
      const buttons = Array.from(panel.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
      if (!buttons.length || document.activeElement === panel.current || document.activeElement === (event.shiftKey ? buttons[0] : buttons.at(-1))) { event.preventDefault(); (event.shiftKey ? buttons.at(-1) : buttons[0])?.focus(); }
    }}>
      <h2 id="reflection-title"><ReflectionIcon />Schau dir deine Lösung noch Mal an - bist du dir sicher?</h2>
      <p className="reflection-countdown" role="timer" aria-live="off">{state?.paused ? "Denkzeit unterbrochen" : remaining > 0 ? `${remaining} ${remaining === 1 ? "Sekunde" : "Sekunden"}` : "Du kannst jetzt entscheiden."}</p>
      <p className="reflection-announcement" role="status">{ready ? "Die Denkzeit ist vorbei. Bestätige oder ändere deine Lösung." : "Schau dir deine Lösung noch einmal an."}</p>
      {error && <p className="alert error" role="alert">{error}</p>}
      <div className="exercise-pause-warning-host" />
      <div className="reflection-actions">{beginPause && <button type="button" className="secondary" disabled={busy} onClick={beginPause}><svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><path d="M8 5v14M16 5v14" /></svg>Pause</button>}<button type="button" className="secondary" disabled={!ready} onClick={() => void decide("edit")}><ReflectionIcon action="edit" />Noch mal ändern</button><button type="button" className="primary" disabled={!ready} onClick={() => void decide("confirm")}><ReflectionIcon action="check" />Ja, ich bin sicher!</button></div>
    </section>}
    </div>, document.body)}
    {!active && error && <p className="alert error" role="alert">{error}</p>}
  </div>;
}
